import React from 'react';
import { Box, Button } from '@mui/material';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type {
  TGuaranteesApplication,
  TPatchGuaranteesIssueGuaranteePayload,
} from '~/store/guarantees/types.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import { UDBTF_GUARANTEE_SCAN_COPY_TEXT } from './constants.ts';
import {
  GuaranteesIssueGuaranteeFieldName,
  type TGuaranteesIssueGuaranteeFormValues,
} from './types.ts';
import {
  buildGuaranteesIssueGuaranteePayload,
  getGuaranteesIssueGuaranteeInitialValues,
  guaranteesIssueGuaranteeResolver,
} from './utils.ts';
import { udbtfGuaranteeIssueDetailsSectionStyles } from './styles.ts';

type TUdbtfGuaranteeIssueDetailsSectionProps = {
  application: TGuaranteesApplication | null;
  isDisabled: boolean;
  isSaving: boolean;
  onSubmit: (payload: TPatchGuaranteesIssueGuaranteePayload) => Promise<void>;
};

const UdbtfGuaranteeIssueDetailsSection: React.FC<TUdbtfGuaranteeIssueDetailsSectionProps> = ({
  application,
  isDisabled,
  isSaving,
  onSubmit,
}) => {
  const defaultValues = React.useMemo(
    () => getGuaranteesIssueGuaranteeInitialValues(application),
    [application]
  );
  const formMethods = useForm<TGuaranteesIssueGuaranteeFormValues>({
    defaultValues,
    resolver: guaranteesIssueGuaranteeResolver,
    mode: 'onSubmit',
  });
  const {
    formState: { isDirty },
    handleSubmit,
    reset,
  } = formMethods;
  const areFieldsDisabled = isDisabled || isSaving;
  const isSaveDisabled = areFieldsDisabled || !isDirty;
  const saveStartIcon = isSaving ? <ButtonLoadingIcon /> : undefined;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleSave: SubmitHandler<TGuaranteesIssueGuaranteeFormValues> = async (values) => {
    try {
      await onSubmit(buildGuaranteesIssueGuaranteePayload(values));
      reset(values);
    } catch {
      // Request feedback is handled by the page-level callback.
    }
  };

  return (
    <FormProvider {...formMethods}>
      <FlowSection
        step={UDBTF_GUARANTEE_SCAN_COPY_TEXT.issueDetailsStep}
        title={UDBTF_GUARANTEE_SCAN_COPY_TEXT.issueDetailsTitle}
        description={UDBTF_GUARANTEE_SCAN_COPY_TEXT.issueDetailsDescription}
      >
        <Box
          component="form"
          onSubmit={(event) => {
            void handleSubmit(handleSave)(event);
          }}
        >
          <Box sx={udbtfGuaranteeIssueDetailsSectionStyles.fieldsGrid}>
            <FormInput
              name={GuaranteesIssueGuaranteeFieldName.ManagementAccountingProduct}
              label={UDBTF_GUARANTEE_SCAN_COPY_TEXT.managementAccountingProductLabel}
              inputProps={{ readOnly: true }}
              sx={[
                guaranteesClientPageStyles.flowField,
                udbtfGuaranteeIssueDetailsSectionStyles.readOnlyField,
              ]}
            />
            <FormDateInput
              name={GuaranteesIssueGuaranteeFieldName.OperationProvidedDate}
              label={UDBTF_GUARANTEE_SCAN_COPY_TEXT.operationProvidedDateLabel}
              disabled={areFieldsDisabled}
              sx={guaranteesClientPageStyles.flowField}
            />
            <FormDateInput
              name={GuaranteesIssueGuaranteeFieldName.OperationFinishedDate}
              label={UDBTF_GUARANTEE_SCAN_COPY_TEXT.operationFinishedDateLabel}
              disabled={areFieldsDisabled}
              sx={guaranteesClientPageStyles.flowField}
            />
          </Box>

          <Box sx={guaranteesClientPageStyles.flowActions}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaveDisabled}
              startIcon={saveStartIcon}
              sx={dataFormStyles.documentPrimaryAction}
            >
              {UDBTF_GUARANTEE_SCAN_COPY_TEXT.saveButton}
            </Button>
          </Box>
        </Box>
      </FlowSection>
    </FormProvider>
  );
};

export default UdbtfGuaranteeIssueDetailsSection;
